import {
  RegistrationAccessFragment,
  RegistrationAccessState
} from "@pathwright/graphql-client/src/generated"

export type RegistrationAccessRow = RegistrationAccessFragment

export type RegistrationAccessIndex = {
  byId: Map<number, RegistrationAccessRow>
  byResourceId: Map<number, RegistrationAccessRow[]>
  byResourceLicenseOfferingId: Map<number, RegistrationAccessRow[]>
}

/** States that still count as "has access" for Offer Resume / Library indicators. */
export const RESUMABLE_ACCESS_STATES: ReadonlySet<RegistrationAccessState> =
  new Set([
    RegistrationAccessState.Active,
    RegistrationAccessState.SubscriptionExpired
  ])

export function indexRegistrationAccesses(
  rows: RegistrationAccessRow[]
): RegistrationAccessIndex {
  const byId = new Map<number, RegistrationAccessRow>()
  const byResourceId = new Map<number, RegistrationAccessRow[]>()
  const byResourceLicenseOfferingId = new Map<number, RegistrationAccessRow[]>()

  for (const row of rows) {
    byId.set(row.id, row)

    const resourceRows = byResourceId.get(row.resource_id) || []
    resourceRows.push(row)
    byResourceId.set(row.resource_id, resourceRows)

    if (row.resource_license_offering_id != null) {
      const offeringRows =
        byResourceLicenseOfferingId.get(row.resource_license_offering_id) || []
      offeringRows.push(row)
      byResourceLicenseOfferingId.set(
        row.resource_license_offering_id,
        offeringRows
      )
    }
  }

  return { byId, byResourceId, byResourceLicenseOfferingId }
}

export function filterResumableAccesses(
  rows: RegistrationAccessRow[]
): RegistrationAccessRow[] {
  return rows.filter((row) => RESUMABLE_ACCESS_STATES.has(row.access_state))
}

export function getAccessesForResource(
  index: RegistrationAccessIndex,
  resourceId: number,
  { resumableOnly = false }: { resumableOnly?: boolean } = {}
): RegistrationAccessRow[] {
  const rows = index.byResourceId.get(resourceId) || []
  return resumableOnly ? filterResumableAccesses(rows) : rows
}

export function getAccessesForResourceLicenseOffering(
  index: RegistrationAccessIndex,
  resourceLicenseOfferingId: number,
  { resumableOnly = false }: { resumableOnly?: boolean } = {}
): RegistrationAccessRow[] {
  const rows =
    index.byResourceLicenseOfferingId.get(resourceLicenseOfferingId) || []
  return resumableOnly ? filterResumableAccesses(rows) : rows
}

/** Earliest future `access_state_valid_until` across rows, or null. */
export function getNearestAccessStateValidUntil(
  rows: RegistrationAccessRow[],
  now: number = Date.now()
): Date | null {
  let nearest: number | null = null

  for (const row of rows) {
    const raw = row.access_state_valid_until
    if (!raw) continue
    const time = new Date(raw).getTime()
    if (Number.isNaN(time) || time <= now) continue
    if (nearest == null || time < nearest) {
      nearest = time
    }
  }

  return nearest == null ? null : new Date(nearest)
}
